/**
 * Supplemental Instructions (SI) React Query hooks.
 *
 * Follows the same pattern as useRFIs.ts — cache invalidation, optimistic
 * updates, and toast notifications consistent with the rest of the app.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type {
  SICreate,
  SIUpdate,
  SIStatusUpdate,
  SendSIEmailRequest,
  SIListOptions,
} from '@/types/supplemental_instructions';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches all SIs for a specific project.
 */
export function useSIs(projectId: string | undefined, options: SIListOptions = {}) {
  return useQuery({
    queryKey: ['sis', projectId, options],
    queryFn: async () => {
      const response = await api.sis.list(projectId!, options);
      return response;
    },
    enabled: !!projectId,
  });
}

/**
 * Fetches a single SI by ID.
 */
export function useSI(siId: string | null | undefined) {
  return useQuery({
    queryKey: ['sis', siId],
    queryFn: async () => {
      const response = await api.sis.get(siId!);
      return response;
    },
    enabled: !!siId,
  });
}

/**
 * Fetches all SIs linked to a specific RFI.
 */
export function useSIsForRFI(rfiId: string | null | undefined) {
  return useQuery({
    queryKey: ['sis', 'rfi', rfiId],
    queryFn: async () => {
      const response = await api.sis.listForRFI(rfiId!);
      return response;
    },
    enabled: !!rfiId,
  });
}

// ============================================================================
// Mutation Hooks — CRUD
// ============================================================================

export function useCreateSI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; si: SICreate }) =>
      api.sis.create(data.projectId, data.si),
    onSuccess: (response) => {
      const newSI = response?.si || response;
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.sis) return old;
          return { ...old, sis: [...old.sis, newSI], total: (old.total ?? 0) + 1 };
        }
      );
      toast.success('Supplemental Instruction created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create SI');
    },
  });
}

/**
 * Creates an SI pre-filled from an answered RFI.
 */
export function useCreateSIFromRFI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, rfiId }: { projectId: string; rfiId: string }) =>
      api.sis.createFromRFI(projectId, rfiId),
    onSuccess: (response) => {
      const newSI = response?.si || response;
      // Invalidate all SI queries for this project
      queryClient.invalidateQueries({ queryKey: ['sis'] });
      toast.success(`SI ${newSI.si_number} created from RFI`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create SI from RFI');
    },
  });
}

export function useUpdateSI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SIUpdate }) =>
      api.sis.update(id, data),
    onSuccess: (response, variables) => {
      const updatedSI = response?.si || response;
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.sis) return old;
          return {
            ...old,
            sis: old.sis.map((s: any) =>
              s.id === variables.id ? { ...s, ...updatedSI } : s
            ),
          };
        }
      );
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey[1] === variables.id },
        (old) => {
          if (!old?.si) return old;
          return { ...old, si: { ...old.si, ...updatedSI } };
        }
      );
      toast.success('SI updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update SI');
    },
  });
}

export function useDeleteSI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siId: string) => api.sis.delete(siId),
    onSuccess: (_, siId) => {
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.sis) return old;
          return {
            ...old,
            sis: old.sis.filter((s: any) => s.id !== siId),
            total: Math.max(0, (old.total ?? 1) - 1),
          };
        }
      );
      toast.success('SI deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete SI');
    },
  });
}

// ============================================================================
// Mutation Hooks — Workflow actions
// ============================================================================

export function useUpdateSIStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siId, status }: { siId: string; status: SIStatusUpdate['status'] }) =>
      api.sis.updateStatus(siId, { status }),
    onSuccess: (response, variables) => {
      const updatedSI = response?.si || response;
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.sis) return old;
          return {
            ...old,
            sis: old.sis.map((s: any) =>
              s.id === variables.siId ? { ...s, ...updatedSI } : s
            ),
          };
        }
      );
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'sis' && query.queryKey[1] === variables.siId },
        (old) => {
          if (!old?.si) return old;
          return { ...old, si: { ...old.si, ...updatedSI } };
        }
      );
      toast.success('SI status updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update SI status');
    },
  });
}

export function useDistributeSI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siId, data }: { siId: string; data: SendSIEmailRequest }) =>
      api.sis.distribute(siId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['sis'] });
      const sentCount = response.sent_to?.length ?? 0;
      toast.success(`SI sent to ${sentCount} recipient${sentCount !== 1 ? 's' : ''}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to distribute SI');
    },
  });
}
